import {
  Evaluation,
  EvaluationInterface,
} from 'app/assessment/evaluation/models';
import { EvaluationActions, EvaluationActionType } from 'app/assessment/evaluation/state/actions';
import { EvaluationState, EvaluationStateInterface } from 'app/assessment/evaluation/state/models/evaluation-state.model';
import { RecommendationInterface } from 'app/recommendation/models';
import { getDeepCopyOfObject } from 'app/shared/utilities/utility-methods.utility';
import { TaskInterface } from 'app/task/models';



const initialState: EvaluationState = new EvaluationState({
  ids: [],
  evaluations: {},
  currentEvaluation: undefined,
  selectedEvaluation: 0,
  currentEvaluationRelatedTask: undefined,
});

export function evaluationReducer(state: EvaluationStateInterface = initialState, action: EvaluationActions): EvaluationStateInterface {
  switch (action.type) {

    // Handle retrieving a fresh load of evaluations
    case EvaluationActionType.LoadEvaluations:
    case EvaluationActionType.LoadEvaluationsFailed:
    case EvaluationActionType.LoadCurrentEvaluation:
    case EvaluationActionType.LoadCurrentEvaluationFailed:
      return {
        ...state,
        ids: [],
        evaluations: {},
        currentEvaluation: undefined,
      };

    // Update the state when we get a new list of evaluations
    case EvaluationActionType.LoadEvaluationsCompleted:
      const allEvaluations: EvaluationInterface[] = action.payload as EvaluationInterface[];
      const newEvaluations: EvaluationInterface[] = allEvaluations.filter(
        (evaluation: EvaluationInterface) => !state.evaluations[evaluation.id]);

      const newEvaluationIds: number[] = newEvaluations.map((evaluation: EvaluationInterface) => evaluation.id);
      const newEvaluationEntities: { [id: number]: EvaluationInterface } = newEvaluations.reduce(
        (evaluations: { [id: number]: EvaluationInterface }, evaluation: EvaluationInterface) => {
          return { ...evaluations, ...{
            [evaluation.id]: evaluation,
          }};
        },
        {});

      return {
        ...state,
        ids: newEvaluationIds,
        evaluations: { ...state.evaluations, ...newEvaluationEntities },
      };

    // Apply the updated evaluation to state
    case EvaluationActionType.UpdateEvaluationCompleted:
      const evaluation: EvaluationInterface = action.payload as EvaluationInterface;
      const updatedEvaluations: { [id: number]: EvaluationInterface } = state.evaluations;
      updatedEvaluations[evaluation.id] = evaluation;

      return {
        ...state,
        evaluations: updatedEvaluations,
      };

    // Update the selected evaluation in state
    case EvaluationActionType.SelectEvaluationCompleted:
      return {
        ...state,
        selectedEvaluation: action.payload as number,
      };

    // Load the current evaluation in state
    case EvaluationActionType.LoadCurrentEvaluationCompleted:
      return {
        ...state,
        currentEvaluation: new Evaluation(action.payload as EvaluationInterface),
      };

    // Update the current evaluation in state
    case EvaluationActionType.UpdateCurrentEvaluationCompleted:
      const updatedEval: EvaluationInterface = new Evaluation(action.payload as EvaluationInterface);
      updatedEval.lastUpdated = new Date();

      return {
        ...state,
        currentEvaluation: updatedEval,
      };

    case EvaluationActionType.UpdateEquipmentRecommendationsFailed:
      const evalCopy: EvaluationInterface = getDeepCopyOfObject(state.currentEvaluation);
      // evalCopy.equipmentRecommendations = new EquipmentOrder({ recommendedEquipment: [] });

      return {
        ...state,
        currentEvaluation: evalCopy,
      };

    // Add recommendation to the current evaluation in state
    case EvaluationActionType.AddEvaluationRecommendationsCompleted:
      const newRecommendation: RecommendationInterface = action.payload as RecommendationInterface;
      const updatedRecommendations: RecommendationInterface[] = state.currentEvaluation.analysis.recommendations
        .map((rec: RecommendationInterface) => rec);

      updatedRecommendations.unshift(newRecommendation);
      return {
        ...state,
        currentEvaluation: {
          ...state.currentEvaluation,
          analysis: {
            ...state.currentEvaluation.analysis,
            recommendations: updatedRecommendations,
          },
        },
      };

    // Update recommendation in the current evaluation in state
    case EvaluationActionType.UpdateEvaluationRecommendationsCompleted:
      const updatedRecommendation: RecommendationInterface = action.payload as RecommendationInterface;
      const updatedEvaluation: EvaluationInterface = getDeepCopyOfObject(state.currentEvaluation);
      updatedEvaluation.analysis.recommendations =
        state.currentEvaluation.analysis.recommendations.map((recommendation: RecommendationInterface) => {
          if(recommendation.id === updatedRecommendation.id) {
            return updatedRecommendation;
          }
          return recommendation;
        });

      return {
        ...state,
        currentEvaluation: updatedEvaluation,
      };

    // Remove recommendation in the current evaluation in state
    case EvaluationActionType.RemoveEvaluationRecommendationsCompleted:
      const removedId: number = (action.payload as RecommendationInterface).id;
      const thisEvaluation: EvaluationInterface = getDeepCopyOfObject(state.currentEvaluation);

      // find recommendation with same id and remove from array
      const removeIndex: number = thisEvaluation.analysis.recommendations
        .map((item: RecommendationInterface) => item.id).indexOf(removedId);
      thisEvaluation.analysis.recommendations.splice(removeIndex, 1);

      return {
        ...state,
        currentEvaluation: thisEvaluation,
      };

    case EvaluationActionType.UpdateCurrentEvaluationRelatedTaskCompleted:
      return {
        ...state,
        currentEvaluationRelatedTask: action.payload as TaskInterface,
      };

    default:
      return state;
  }
}
